import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { apiClient } from '../api/client';
import LiveMap from '../components/LiveMap';
import OrdersPanel from '../components/OrdersPanel';
import MapSidePanel from '../components/MapSidePanel';
import LoadingIndicator from '../components/LoadingIndicator';
import PageHeader from '../components/PageHeader';
import StatBar from '../components/StatBar';
import { formatMoney } from '../utils/money';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { useTechnicians } from '../hooks/useTechnicians';

interface DashboardStats {
  todayCount: number;
  overdueCount: number;
  unassignedCount: number;
  nightPendingCount: number;
  techniciansOnShift: number;
  revenueToday: number;
}

// То же окно, что и на бэкенде (OrdersService.isManualOrderWindowNow) — днём заявки
// должны идти через Битрикс, кнопка ручного создания видна только диспетчеру ночной смены
function isManualOrderWindowNow(): boolean {
  const hour = new Date().getHours();
  return hour >= 20 || hour < 8;
}

// По умолчанию открывается "Карта" — Канбан (dnd-kit) и Календарь (react-big-calendar
// + date-fns) заметно тяжелят бандл, поэтому грузим их только когда диспетчер реально
// переключится на эти вкладки
const KanbanBoard = lazy(() => import('../components/KanbanBoard'));
const CalendarView = lazy(() => import('../components/CalendarView'));

type View = 'map' | 'list' | 'kanban' | 'calendar';

const TABS: { id: View; label: string }[] = [
  { id: 'map', label: 'Карта' },
  { id: 'list', label: 'Список' },
  { id: 'kanban', label: 'Канбан' },
  { id: 'calendar', label: 'Календарь' },
];

const VIEW_VALUES: View[] = ['map', 'list', 'kanban', 'calendar'];

export default function DashboardPage() {
  const navigate = useNavigate();
  // Вкладка хранится в адресе страницы — при перезагрузке (F5) остаёмся там же,
  // где были, а не прыгаем обратно на "Карту"
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view: View = VIEW_VALUES.includes(requestedView as View) ? (requestedView as View) : 'map';
  const setView = (next: View) => setSearchParams({ view: next }, { replace: true });

  // Полная карточка наряда — отдельная страница (screen 05 макета), открывается по клику
  // из любой вкладки (Карта/Список/Канбан/Календарь)
  const openOrder = (orderId: string) => navigate(`/orders/${orderId}`);
  const technicians = useTechnicians();
  const activeTechnicianCount = technicians.filter((t) => t.isActive !== false).length;

  const [stats, setStats] = useState<DashboardStats | null>(null);
  function loadStats() {
    apiClient
      .get<DashboardStats>('/orders/stats/dashboard')
      .then(({ data }) => setStats(data))
      .catch((err) => console.error('Не удалось загрузить статистику диспетчерской', err));
  }
  useEffect(() => {
    loadStats();
    // Живое обновление уже приходит по сокету (useBoardUpdates ниже) — интервал только
    // подстраховка на случай обрыва сокета, и чтобы "Мастеров на смене"/"Просрочено" не
    // застревали между событиями (эти два числа сами по себе не связаны ни с одним событием)
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useBoardUpdates(['orders', 'acts'], loadStats);

  return (
    <div className="dashboard">
      <PageHeader title="Диспетчерская" subtitle="Карта, список, канбан и календарь нарядов в реальном времени">
        <div className="view-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`view-tab ${view === tab.id ? 'active' : ''}`}
              onClick={() => setView(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {isManualOrderWindowNow() && (
          <button onClick={() => navigate('/orders/new')}>
            <Plus size={14} /> Новый наряд
          </button>
        )}
      </PageHeader>

      {stats && (
        <StatBar
          stats={[
            { label: 'Нарядов сегодня', value: String(stats.todayCount) },
            { label: 'Просрочено', value: String(stats.overdueCount), tone: stats.overdueCount > 0 ? 'danger' : 'default' },
            { label: 'Без мастера', value: String(stats.unassignedCount) },
            { label: 'Ночных заявок', value: String(stats.nightPendingCount) },
            {
              label: 'Мастеров на смене',
              value: activeTechnicianCount > 0 ? `${stats.techniciansOnShift} / ${activeTechnicianCount}` : String(stats.techniciansOnShift),
            },
            { label: 'Выручка за день', value: formatMoney(stats.revenueToday) },
          ]}
        />
      )}

      <div className="dashboard-body">
        {view === 'map' && (
          <div className="dashboard-map-layout">
            <div className="dashboard-map">
              <LiveMap />
            </div>
            <MapSidePanel onOrderClick={openOrder} />
          </div>
        )}
        {view === 'list' && <OrdersPanel onOrderClick={openOrder} />}
        {view === 'kanban' && (
          <Suspense fallback={<LoadingIndicator />}>
            <KanbanBoard onOrderClick={openOrder} />
          </Suspense>
        )}
        {view === 'calendar' && (
          <Suspense fallback={<LoadingIndicator />}>
            <CalendarView onOrderClick={openOrder} />
          </Suspense>
        )}
      </div>
    </div>
  );
}
